import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, MessageSquare, KeyRound, ScanLine, BookText, Images, Bell, Sparkles } from "lucide-react";
import PhoneFrame from "@/components/PhoneFrame";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";

export default function Landing() {
  const nav = useNavigate();
  const features = [
    { icon: BookText,      label: "Journal",       note: "AI-polished writing" },
    { icon: ScanLine,      label: "Scanner + OCR", note: "Snap. Filed." },
    { icon: Shield,        label: "Docs Vault",    note: "Aadhaar, PAN, bills" },
    { icon: KeyRound,      label: "Passwords",     note: "Encrypted + history" },
    { icon: Bell,          label: "Reminders",     note: "Never miss what matters" },
    { icon: Images,        label: "Media",         note: "All your memories" },
  ];
  return (
    <PhoneFrame>
      <div className="flex flex-col h-full min-h-[100dvh]">
        {/* ── Hero ─────────────────────────────────────────────── */}
        <div className="px-6 pt-12 pb-6 bg-[radial-gradient(600px_circle_at_50%_0%,hsl(var(--accent)),transparent_60%)]">
          <div className="flex items-center justify-between">
            <Logo size={30} />
            <ThemeToggle size="sm" />
          </div>
          <h1 className="font-display font-bold text-[44px] leading-[1.02] tracking-tight mt-8">
            One app.<br/>
            <span className="text-primary">Everything</span><br/>
            important.
          </h1>
          <p className="text-[15px] text-muted-foreground mt-4 max-w-[300px] leading-relaxed">
            Your AI-powered personal memory. Notes, journal, documents, passwords, media — all in one private conversation.
          </p>
        </div>

        {/* ── HERO CTA: AI Chat card (the star of the show) ────── */}
        <div className="px-5 mt-1">
          <button
            type="button"
            onClick={() => nav("/auth?mode=register")}
            className="w-full rounded-3xl p-5 flex items-center gap-4 text-left bg-primary text-primary-foreground shadow-[0_16px_40px_-12px_hsl(var(--primary)/0.55)] active:scale-[0.99] transition"
            data-testid="landing-hero-chat-cta"
          >
            <div className="h-14 w-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
              <MessageSquare className="h-7 w-7" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-[0.16em] font-semibold opacity-80">Meet Impo</span>
                <Sparkles className="h-3 w-3 opacity-80" />
              </div>
              <div className="font-display font-bold text-xl leading-tight mt-0.5">
                AI Chat that remembers everything
              </div>
              <div className="text-[12px] opacity-85 mt-0.5">Say it. Save it. Ask for it later.</div>
            </div>
          </button>
        </div>

        {/* ── Features section ─────────────────────────────────── */}
        <div className="px-5 mt-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Features</div>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {features.map(({ icon: Icon, label, note }) => (
              <div
                key={label}
                className="rounded-2xl border border-border bg-card p-3"
                data-testid={`landing-feature-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="h-8 w-8 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="font-medium mt-2 text-[13px] leading-tight">{label}</div>
                <div className="text-[10.5px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">{note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer CTAs ──────────────────────────────────────── */}
        <div className="mt-auto p-5 pt-6 space-y-2">
          <Button
            className="w-full h-12 text-base font-semibold"
            onClick={() => nav("/auth?mode=register")}
            data-testid="landing-get-started-button"
          >
            Get started
          </Button>
          <button
            onClick={() => nav("/auth?mode=login")}
            className="w-full text-sm text-muted-foreground py-2 font-medium hover:text-foreground transition"
            data-testid="landing-sign-in-link"
          >
            I already have an account
          </button>
          {/* Legal footer — required for Play Store / App Store submissions.
              Keep this on the public Landing page so store reviewers can
              reach the privacy & terms surfaces without signing in. */}
          <div className="pt-2 text-center text-[11px] text-muted-foreground space-x-3">
            <a href="/privacy" className="hover:text-foreground underline-offset-2 hover:underline" data-testid="landing-privacy-link">Privacy</a>
            <span aria-hidden>·</span>
            <a href="/terms" className="hover:text-foreground underline-offset-2 hover:underline" data-testid="landing-terms-link">Terms</a>
            <span aria-hidden>·</span>
            <a href="mailto:hello@impoapp.com" className="hover:text-foreground underline-offset-2 hover:underline" data-testid="landing-contact-link">Contact</a>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
